import { Icon } from '@dnb/eufemia';
import { chevron_down_medium as ChevronDownIcon, chevron_right_medium as ChevronRightIcon } from '@dnb/eufemia/icons';
import { Fragment, JSX, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import remarkParse from 'remark-parse';
import slugify from 'slugify';
import { unified } from 'unified';

import { useScrollSpy } from '@/hooks/useScrollSpy';

import Search from './Search';

interface NavigationType {
  name: string;
  description: string;
  parent?: string;
  key: string;
  children: NavigationType[];
}

const flattenArr = (arr: NavigationType[]) => {
  const result: NavigationType[] = [];

  arr.forEach((item) => {
    const { name, key, children, description, parent } = item;

    result.push({ name, key, description, children: [], parent });

    if (children) result.push(...flattenArr(children));
  });

  return result;
};

const extractHeadings = (markdownContent: string) => {
  const processor = unified().use(remarkParse);
  const ast = processor.parse(markdownContent);

  const headings: { level: number; text: string }[] = [];
  ast.children.forEach((node) => {
    if (node.type === 'heading' && (node.depth === 1 || node.depth === 2)) {
      headings.push({
        level: node.depth,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        text: node.children.map((child: any) => child.value).join(''),
      });
    }
  });

  return headings;
};

export function extractLinks(content: string) {
  const parsedData = extractHeadings(content);
  const myLinks: NavigationType[] = [];
  const hasH1 = parsedData.length > 0 && parsedData[0]!.level === 1;

  parsedData.forEach((item) => {
    const name = item.text;
    if (item.level === 2 && hasH1) {
      myLinks.at(-1)?.children.push({
        name,
        parent: myLinks.at(-1)?.key,
        description: name,
        key: 'heading/' + slugify(name.toLowerCase(), { trim: false }),
        children: [],
      });
    } else {
      myLinks.push({
        name,
        description: name,
        key: 'heading/' + slugify(name.toLowerCase(), { trim: false }),
        children: [],
      });
    }
  });

  return myLinks;
}

const GuideTableOfContent = ({ guide }: { guide: string | null | undefined }): JSX.Element => {
  const navigate = useNavigate();
  const links = useMemo(() => (guide ? extractLinks(guide) : undefined), [guide]);
  const flattenLinks = useMemo(() => (links ? flattenArr(links) : []), [links]);
  const selectedItem = useScrollSpy(flattenLinks.map((li) => li.key));
  const [selectedMenuItem, setSelectedMenuItem] = useState<NavigationType | undefined>();
  const [search, setSearch] = useState<string[] | undefined>();
  //State for checking the initial loading. Needed for the autoscroll to work.
  const [initiallyLoaded, setInitiallyLoaded] = useState(false);

  useEffect(() => {
    setSelectedMenuItem(flattenLinks.find((item) => item.key === selectedItem));
    //Set hash url to be empty when it is the first item, no need to autoscroll
    const hashUrl = flattenLinks[0]?.key === selectedItem ? '' : selectedItem;
    if (selectedItem && initiallyLoaded) navigate(`#${hashUrl}`, { replace: true });
  }, [navigate, selectedItem, flattenLinks, guide, initiallyLoaded]);

  useEffect(() => {
    if (guide)
      setTimeout(() => {
        setInitiallyLoaded(true);
        document.getElementById(location.hash.replace('#', ''))?.scrollIntoView({ behavior: 'auto' });
      }, 500);
  }, [guide]);

  const renderLink = (link: NavigationType) => {
    const active = [selectedMenuItem?.key, selectedMenuItem?.parent].includes(link.key);

    return (
      <Fragment key={`linkGroup-${link.key}`}>
        <li className={`${active ? 'active' : ''}`} key={`ref-links-${link.key}`}>
          <a href={`#${link.key}`}>
            {link.name}
            {link.children.length > 0 && (
              <Icon color="var(--color-sea-green)" icon={active ? ChevronDownIcon : ChevronRightIcon} />
            )}
          </a>
        </li>
        {active && link.children.length > 0 && (
          <ul>
            {link.children.map((sub) => (
              <li className={`sub ${selectedItem === sub.key ? 'active' : ''}`} key={`ref-links-${sub.key}`}>
                <a href={`#${sub.key}`}>{sub.name}</a>
              </li>
            ))}
          </ul>
        )}
      </Fragment>
    );
  };

  return (
    <div className="__left">
      <div className="sticker">
        {flattenLinks && <Search items={flattenLinks} searchResult={setSearch} />}
        <div className="scroll-content">
          {links && (
            <ul>
              {search
                ? flattenLinks.filter((link) => search.includes(link.name)).map(renderLink)
                : links.map(renderLink)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuideTableOfContent;
